import { useEffect, useRef, useState } from 'react';
import { MapPin, Search, X } from 'lucide-react';
import { useInfiniteCities } from '@/features/location/api/useLocationQueries';
import type { CityLocation } from '@/features/location/api/locationApi';
import { useDebounce } from '@/shared/hooks/useDebounce';


function isComingSoonCity(city: CityLocation) {
  const status = String(city.status || '').toLowerCase();
  return (
    status.includes('upcoming') ||
    status.includes('coming') ||
    status.includes('onboarding')
  );
}

type CityChangeModalProps = {
  open: boolean;
  selectedCity?: string;
  onClose: () => void;
  onSelect: (city: string) => void;
};

export function CityChangeModal({
  open,
  selectedCity,
  onClose,
  onSelect,
}: CityChangeModalProps) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
  } = useInfiniteCities(debouncedSearch, open);

  const cities = data?.pages.flatMap((page) => page.data ?? []) ?? [];

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    if (!open || !loadMoreRef.current) return;

    const node = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '80px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage, cities.length]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
      }}
    >
      
      <button
        type="button"
        aria-label="Close city modal"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(30, 27, 58, 0.5)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: 'none',
          cursor: 'pointer',
        }}
        onClick={onClose}
      />
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          width: '520px',
          maxWidth: '100%',
          maxHeight: '92vh',
          backgroundColor: '#FFFFFF',
          borderRadius: '28px',
          boxShadow: '0 30px 80px rgba(20, 15, 60, 0.4)',
          overflow: 'hidden',
          color: '#3A3660',
        }}
      >
        <div
          style={{
            padding: '24px 28px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div style={{ textAlign: 'left' }}>
            <h2
              style={{
                fontSize: '20px',
                fontWeight: 800,
                color: '#3A3660',
                margin: 0,
                lineHeight: 1.25,
                letterSpacing: '-0.02em',
              }}
            >
              I&apos;m looking to{' '}
              <span style={{ color: '#3B66B8' }}>
                advertise
              </span>{' '}
              in
            </h2>
            <p
              style={{
                fontSize: '13px',
                color: '#6A6688',
                marginTop: '4px',
                marginBottom: 0,
                fontWeight: 500,
              }}
            >
              Choose your target city
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '1px solid #EAE8F5',
              background: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              cursor: 'pointer',
              color: '#4A4670',
              flexShrink: 0,
              transition: 'background-color .15s ease, border-color .15s ease',
            }}
          >
            <X size={18} strokeWidth={1.8} />
          </button>
        </div>

        <div
          style={{
            padding: '0 28px 20px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <label
            style={{
              display: 'block',
              textAlign: 'left',
              fontSize: '11px',
              fontWeight: 800,
              letterSpacing: '1px',
              color: '#6A6688',
              textTransform: 'uppercase',
              marginBottom: '8px',
            }}
          >
            SEARCH CITIES
          </label>
          <div
            style={{
              height: '48px',
              borderRadius: '14px',
              background: '#F3F2F9',
              border: '1px solid #EAE8F5',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '0 14px',
              flexShrink: 0,
            }}
          >
            <Search size={18} strokeWidth={1.8} style={{ color: '#8156B4', flexShrink: 0 }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search for a city..."
              aria-label="Search a city"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: '14px',
                fontWeight: 500,
                color: '#3A3660',
                width: '100%',
              }}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-[#E4E8F0] bg-[#FCFDFE]">
            <div className="border-b border-[#EEF1F4] bg-[#F7F8FA] px-4 py-2.5">
              <p className="text-left text-xs font-semibold text-[#6B7280]">
                All available locations
              </p>
            </div>
            <div className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
              {isPending ? (
                <p className="py-8 text-center text-sm text-[#9CA3AF]">
                  Loading cities…
                </p>
              ) : cities.length === 0 ? (
                <p className="py-8 text-center text-sm text-[#9CA3AF]">
                  City not found
                </p>
              ) : (
                <>
                  {cities.map((city, index) => {
                    const name = city.city || '';
                    const comingSoon = isComingSoonCity(city);
                    if (comingSoon) {
                      return (
                        <div
                          key={`${name}-${index}`}
                          aria-disabled="true"
                          className="flex w-full items-center gap-2 rounded-xl border-l-4 border-l-transparent px-3 py-2.5 text-left"
                        >
                          <MapPin size={14} className="shrink-0 text-[#C5CAD3]" />
                          <span className="text-sm font-medium text-[#9CA3AF]">
                            {name}
                          </span>
                          <span className="ml-auto shrink-0 rounded-full bg-[#FFF6E8] px-2.5 py-1 text-[11px] font-semibold whitespace-nowrap text-[#C2410C]">
                            Onboarding coming soon
                          </span>
                        </div>
                      );
                    }
                    const active = selectedCity === name;
                    return (
                      <button
                        key={`${name}-${index}`}
                        type="button"
                        onClick={() => {
                          if (!name) return;
                          onSelect(name);
                          setSearch('');
                        }}
                        className={`flex w-full cursor-pointer items-center gap-2 rounded-xl border-l-4 px-3 py-2.5 text-left transition ${
                          active
                            ? 'border-l-[#119C84] bg-[#F0F9F7]'
                            : 'border-l-transparent hover:bg-[#F7F8FA]'
                        }`}
                      >
                        <MapPin
                          size={14}
                          className={active ? 'text-[#119C84]' : 'text-[#9CA3AF]'}
                        />
                        <span className="text-sm font-medium text-[#0F1817]">
                          {name}
                        </span>
                      </button>
                    );
                  })}
                  <div
                    ref={loadMoreRef}
                    className="py-3 text-center text-xs text-[#9CA3AF]"
                  >
                    {isFetchingNextPage
                      ? 'Loading more cities…'
                      : hasNextPage
                        ? 'Scroll for more'
                        : null}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
